/**
 * cd2bPricing — ESPEJO en el cliente del motor de precio del backend
 * (`api/src/services/cd2bPricingService.cotizar`). Sirve para pintar el total EN VIVO mientras el
 * asesor marca módulos, sin un round-trip por tecla. La AUTORIDAD sigue siendo el backend: al
 * guardar/cotizar se consulta `/cd2b/propuestas/cotizar` y ese número manda. Si este espejo
 * divergiera, el asesor vería un total y cobraría otro — por eso consume los MISMOS datos que el
 * backend ya calculó en `getCatalogo` (precioBase por edición, precio/optIn por módulo), en vez de
 * recomputar precios aquí.
 *
 * @typedef {{edicion:string, areas:string[], precioBase:number, modulosIncluidos:string[], seatsIncluidos:{completo:number,operativo:number}}} EdicionCat
 * @typedef {{code:string, label:string, areas:string[], precio:number, optIn:boolean, incluidoEn:string[]}} ModuloCat
 */

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const norm = (modulos) =>
  [...new Set((modulos || []).map((m) => (typeof m === 'string' ? m : m && m.code)).filter(Boolean))];

/**
 * Cotiza un paquete con el catálogo ya enriquecido (el de `getCatalogo`/ficha).
 *
 * @param {{edicion?:string, modulos?:any[], seats?:{completo?:number,operativo?:number}, addons?:string[], cargos?:any[], moneda?:string}} input
 * @param {{ediciones:EdicionCat[], modulos:ModuloCat[], precios?:{seatPrecio?:{completo:number,operativo:number}}}} catalogo
 */
export function cotizar(input = {}, catalogo = {}) {
  const ediciones = catalogo.ediciones || [];
  const modulos = catalogo.modulos || [];
  const seatPrecio = catalogo.precios?.seatPrecio || { completo: 0, operativo: 0 };

  const ed = ediciones.find((e) => e.edicion === input.edicion) || ediciones.find((e) => e.edicion === 'completa') || ediciones[0] || {};
  const incluidos = new Set(ed.modulosIncluidos || []);
  const modById = new Map(modulos.map((m) => [m.code, m]));
  const marcados = norm(input.modulos);

  // CIERRE DE DEPENDENCIAS, espejo de `cerrarSeleccionModulos` del backend: lo marcado puede
  // exigir módulos que no lo están (Reparto necesita Ventas) y ahora se COBRAN.
  //
  // Una dependencia está cubierta si está marcada o si la EDICIÓN ya trae sus áreas: el cliente
  // las recibe por el precio de la edición (ya no hay recorte por paquete) y no se le cobran dos
  // veces. Mismo criterio que el servidor.
  const areasEdicion = new Set(ed.areas || []);
  const cubiertaPorEdicion = (code) => {
    const areas = modById.get(code)?.areas || [];
    return areas.length > 0 && areas.every((a) => areasEdicion.has(a));
  };
  const sel = [...marcados];
  const dependencias = [];
  if (sel.length) {
    for (let i = 0; i < sel.length; i += 1) {
      for (const dep of modById.get(sel[i])?.requiere || []) {
        if (sel.includes(dep) || cubiertaPorEdicion(dep)) continue;
        sel.push(dep);
        dependencias.push({
          code: dep,
          label: modById.get(dep)?.label || dep,
          requeridoPorLabel: modById.get(sel[i])?.label || sel[i],
        });
      }
    }
  }
  const porQue = new Map(dependencias.map((d) => [d.code, d]));

  const basePrecio = r2(ed.precioBase || 0);

  // EXTRAS: seleccionados que NO vienen en el plan (incluye verticales opt-in).
  const modulosExtra = sel
    .filter((c) => !incluidos.has(c))
    .map((c) => {
      const m = modById.get(c) || {};
      return {
        code: c, label: m.label || c, precio: r2(m.precio || 0), optIn: !!m.optIn,
        requeridoPor: porQue.get(c)?.requeridoPorLabel || null,
      };
    });

  // ASIENTOS extra sobre el cupo de la edición.
  const inc = ed.seatsIncluidos || { completo: 0, operativo: 0 };
  const compN = Math.max(0, Math.floor(Number(input.seats?.completo) || 0) - (inc.completo || 0));
  const opN = Math.max(0, Math.floor(Number(input.seats?.operativo) || 0) - (inc.operativo || 0));
  const seatsExtra = {
    completo: { n: compN, precioUnit: r2(seatPrecio.completo), precio: r2(compN * (seatPrecio.completo || 0)) },
    operativo: { n: opN, precioUnit: r2(seatPrecio.operativo), precio: r2(opN * (seatPrecio.operativo || 0)) },
  };

  // ADD-ONS por código (negocio/soporte) — precio del mapa crudo. El label y la familia salen
  // del catálogo del backend (`addons.*`), que ya los trae: sin esto la línea mostraba el
  // CÓDIGO CRUDO ("cd2b-soporte-premium") en el desglose y en la cotización impresa.
  const precioModulo = catalogo.precios?.precioModulo || {};
  const defsAddon = [
    catalogo.addons?.seats?.completo,
    catalogo.addons?.seats?.operativo,
    catalogo.addons?.negocio,
    ...(catalogo.addons?.capacidad || []),
    // Canales de acceso (la app del teléfono): faltaban aquí, así que el espejo cobraba el
    // precio crudo de `precioModulo` incluso cuando la edición ya lo trae gratis.
    ...(catalogo.addons?.acceso || []),
    ...(catalogo.addons?.soporte || []),
  ].filter(Boolean);
  const addonPorCode = new Map(defsAddon.map((a) => [a.code, a]));
  const addons = norm(input.addons).map((c) => {
    const def = addonPorCode.get(c) || {};
    // `incluidoEn`: ediciones que ya lo traen gratis (backend `cd2bCatalogo.incluidoDesdeEdicion`,
    // hoy solo app móvil en Emprendedor/Micro) — no se cobra aparte aunque esté marcado.
    const incluido = (def.incluidoEn || []).includes(ed.edicion);
    return { code: c, tipo: def.tipo || def.kind || 'modulo', label: def.label || c, precio: incluido ? 0 : r2(precioModulo[c] || def.precio || 0), incluido };
  });

  // PAQUETES DE CAPACIDAD (productos / DTE al mes): se cobran por unidad y cada una sube el tope
  // un `paso`. El paso, el precio y el tope base los manda el BACKEND en el catálogo
  // (`addons.capacidad` + `limitesPorEdicion`): este espejo no tiene tabla propia que divergir.
  const defsCapacidad = catalogo.addons?.capacidad || [];
  const capacidad = (input.capacidad || [])
    .map((c) => {
      const def = defsCapacidad.find((d) => d.code === (typeof c === 'string' ? c : c?.code));
      const cantidad = Math.max(0, Math.floor(Number(typeof c === 'string' ? 1 : c?.cantidad ?? 1) || 0));
      if (!def || cantidad === 0) return null;
      return {
        code: def.code,
        cantidad,
        precioUnit: r2(def.precio),
        precio: r2(cantidad * (def.precio || 0)),
        paso: def.paso || 0,
        limite: def.limite || null,
        unidades: (def.paso || 0) * cantidad,
      };
    })
    .filter(Boolean);

  // Topes RESULTANTES: base de la edición + lo que suman los paquetes. `null` = sin tope, y sobre
  // un plan sin tope el paquete no aplica (misma regla que `limitesEfectivos` del catálogo).
  const limitesBase = catalogo.limitesPorEdicion?.[ed.edicion] || {};
  const limites = { productos: limitesBase.productos ?? null, dteMes: limitesBase.dteMes ?? null };
  for (const c of capacidad) {
    if (c.limite && limites[c.limite] != null) limites[c.limite] += c.unidades;
  }

  // CARGOS adicionales.
  const cargos = input.cargos || [];
  const cargosUnicos = cargos.filter((c) => (c.frequency || 'once') === 'once').map((c) => ({ code: c.code || null, name: c.name, amount: r2(c.amount) }));
  const cargosRecurrentes = cargos
    .filter((c) => (c.frequency || 'once') !== 'once')
    .map((c) => ({ code: c.code || null, name: c.name, amount: r2(c.amount), frequency: c.frequency, mensualizado: c.frequency === 'annual' ? r2((Number(c.amount) || 0) / 12) : r2(c.amount) }));

  const sumaModulosExtra = r2(modulosExtra.reduce((s, m) => s + m.precio, 0));
  const sumaSeats = r2(seatsExtra.completo.precio + seatsExtra.operativo.precio);
  const sumaAddons = r2(addons.reduce((s, a) => s + a.precio, 0));
  const sumaCapacidad = r2(capacidad.reduce((s, c) => s + c.precio, 0));
  const sumaCargosRec = r2(cargosRecurrentes.reduce((s, c) => s + c.mensualizado, 0));

  const totalMensualBruto = r2(basePrecio + sumaModulosExtra + sumaSeats + sumaAddons + sumaCapacidad + sumaCargosRec);

  // DESCUENTO / PROMOCIÓN sobre el mensual (espejo del backend). No toca los cargos únicos.
  const d = input.descuento && Number(input.descuento.valor) > 0 ? input.descuento : null;
  const descuentoMensual = !d ? 0
    : d.tipo === 'percent'
      ? r2(totalMensualBruto * Math.min(100, Math.max(0, Number(d.valor))) / 100)
      : r2(Math.min(Math.max(0, Number(d.valor)), totalMensualBruto));
  const totalMensual = r2(totalMensualBruto - descuentoMensual);
  const descuento = d ? {
    tipo: d.tipo, valor: Number(d.valor), motivo: d.motivo || '',
    mesesPromo: Number(d.mesesPromo) > 0 ? Math.floor(Number(d.mesesPromo)) : null,
    monto: descuentoMensual, precioFull: totalMensualBruto, precioPromo: totalMensual,
  } : null;

  const totalUnico = r2(cargosUnicos.reduce((s, c) => s + c.amount, 0));

  // Cada línea lleva su FAMILIA (`tipo`) y su `code`, espejando al backend: es lo que permite
  // agrupar y poner icono en el panel y en el documento del cliente sin adivinar por el texto.
  const desglose = [];
  const linea = (tipo, code, concepto, detalle, precio) => desglose.push({ tipo, code, concepto, detalle, precio });

  linea('plan', null, `Plan ${ed.label || ed.edicion || ''}`, `${(ed.modulosIncluidos || []).length} módulos incluidos`, basePrecio);
  for (const m of modulosExtra) {
    linea(
      'modulo', m.code, m.label,
      m.requeridoPor ? `Requerido por ${m.requeridoPor}` : (m.optIn ? 'Vertical (add-on)' : 'Módulo adicional'),
      m.precio,
    );
  }
  if (seatsExtra.completo.n) linea('asiento', catalogo.addons?.seats?.completo?.code || null, `${seatsExtra.completo.n} asiento(s) completo`, null, seatsExtra.completo.precio);
  if (seatsExtra.operativo.n) linea('asiento', catalogo.addons?.seats?.operativo?.code || null, `${seatsExtra.operativo.n} asiento(s) operativo`, null, seatsExtra.operativo.precio);
  for (const a of addons) linea(a.tipo, a.code, a.label, null, a.precio);
  for (const c of capacidad) {
    const unidad = c.limite === 'dteMes' ? 'DTE al mes' : 'productos';
    // Plural del NÚCLEO del sintagma: el sufijo -s automático daba "2 paquete de productoss".
    const et = c.cantidad > 1
      ? (c.limite === 'dteMes' ? 'paquetes de DTE' : 'paquetes de productos')
      : (c.limite === 'dteMes' ? 'paquete de DTE' : 'paquete de productos');
    linea('capacidad', c.code, `${c.cantidad} ${et}`, `+${c.unidades} ${unidad}`, c.precio);
  }
  for (const c of cargosRecurrentes) linea('servicio', c.code || null, c.name || 'Cargo recurrente', c.frequency === 'annual' ? 'anual (÷12)' : 'mensual', c.mensualizado);
  if (descuentoMensual > 0) {
    linea(
      'descuento', null,
      'Descuento' + (descuento.motivo ? ` · ${descuento.motivo}` : ''),
      descuento.tipo === 'percent' ? `${descuento.valor}%${descuento.mesesPromo ? ` · ${descuento.mesesPromo} meses` : ''}` : (descuento.mesesPromo ? `${descuento.mesesPromo} meses` : ''),
      -descuentoMensual,
    );
  }

  return {
    edicion: ed.edicion,
    base: { edicion: ed.edicion, label: ed.label, precio: basePrecio, incluye: { modulos: ed.modulosIncluidos || [], seats: inc } },
    modulosExtra,
    // Módulos que entraron solos por dependencia (con su porqué). La UI los señala para que el
    // asesor no crea que la aplicación le añadió un cobro por su cuenta.
    dependencias,
    // Selección REAL una vez cerradas las dependencias: es la que hay que guardar y enviar al
    // backend, no la que el asesor marcó.
    modulos: sel,
    seatsExtra,
    addons,
    capacidad,
    limites,
    cargosUnicos,
    cargosRecurrentes,
    descuento,
    totalMensualBruto,
    totalMensual,
    totalUnico,
    moneda: input.moneda || 'USD',
    desglose,
  };
}

// ============================================================================
// REVENTA CON MARGEN PROPIO (docs/120, F1) — aplicarMargen(), ESPEJO LITERAL de
// `api/src/services/cd2bPricingService.js` (misma función, mismo nombre). Si diverge del
// backend, el asesor ve un precio en el simulador y le cobra otro al cliente al guardar —
// por eso hay un test de paridad (`cd2bAplicarMargenParidad.test.js`) que corre los MISMOS
// casos contra las dos copias y exige el mismo resultado byte a byte.
// ============================================================================

export const DESCUENTO_EXCEDE_MARGEN = 'DESCUENTO_EXCEDE_MARGEN';

function margenDeOverride(politica, tipo, code) {
  if (!politica || !Array.isArray(politica.overrides)) return null;
  return politica.overrides.find((o) => o && o.tipo === tipo && o.code === code) || null;
}

function margenPctGlobal(politica) {
  return Math.max(0, Number(politica?.margenGlobalPct) || 0);
}

function margenDeLinea(politica, { tipo, code, costo, cantidadUnidades = 1 }) {
  const costoN = Math.max(0, Number(costo) || 0);
  const ov = margenDeOverride(politica, tipo, code);
  let margenBruto;
  if (ov && ov.margenFijo != null) {
    margenBruto = r2(Number(ov.margenFijo) * Math.max(1, Number(cantidadUnidades) || 1));
  } else {
    const pct = ov && ov.margenPct != null ? Number(ov.margenPct) : margenPctGlobal(politica);
    margenBruto = r2((costoN * Math.max(0, pct)) / 100);
  }
  margenBruto = Math.max(0, margenBruto);
  const tope = politica && politica.topeVigentePct != null ? Math.max(0, Number(politica.topeVigentePct) || 0) : null;
  const margen = tope != null ? Math.min(margenBruto, r2((costoN * tope) / 100)) : margenBruto;
  return { costo: r2(costoN), margen: r2(margen), venta: r2(costoN + margen) };
}

/**
 * Aplica la política de margen de un asesor a una cotización de `cotizar()`. `catalogo` es el
 * mismo objeto ya pasado a `cotizar()` — aquí solo se usa para leer los `code` de asiento (la
 * salida de `cotizar()` no los trae, a propósito: `cotizar()` no se toca en este PRD).
 *
 * @param {ReturnType<typeof cotizar>} cot
 * @param {object|null} politica
 * @param {object} [catalogo]
 */
export function aplicarMargen(cot, politica, catalogo = {}) {
  const pol = politica || null;
  const codeSeatCompleto = catalogo.addons?.seats?.completo?.code || null;
  const codeSeatOperativo = catalogo.addons?.seats?.operativo?.code || null;
  const lineas = [];
  let costoMensual = 0;
  let margenBrutoMensual = 0;

  const plan = margenDeLinea(pol, { tipo: 'plan', code: cot.edicion, costo: cot.base.precio });
  lineas.push({ tipo: 'plan', code: null, concepto: `Plan ${cot.base.label}`, ...plan });
  costoMensual += plan.costo;
  margenBrutoMensual += plan.margen;

  for (const m of cot.modulosExtra) {
    const l = margenDeLinea(pol, { tipo: 'modulo', code: m.code, costo: m.precio });
    lineas.push({ tipo: 'modulo', code: m.code, concepto: m.label, ...l });
    costoMensual += l.costo;
    margenBrutoMensual += l.margen;
  }

  for (const [clave, code, etiqueta] of [
    ['completo', codeSeatCompleto, 'completo'],
    ['operativo', codeSeatOperativo, 'operativo'],
  ]) {
    const s = cot.seatsExtra?.[clave];
    if (s && s.n > 0) {
      const l = margenDeLinea(pol, { tipo: 'asiento', code, costo: s.precio, cantidadUnidades: s.n });
      lineas.push({ tipo: 'asiento', code, concepto: `${s.n} asiento(s) ${etiqueta}`, ...l });
      costoMensual += l.costo;
      margenBrutoMensual += l.margen;
    }
  }

  for (const a of cot.addons) {
    const l = margenDeLinea(pol, { tipo: a.tipo, code: a.code, costo: a.precio });
    lineas.push({ tipo: a.tipo, code: a.code, concepto: a.label, ...l });
    costoMensual += l.costo;
    margenBrutoMensual += l.margen;
  }

  for (const c of cot.capacidad) {
    const l = margenDeLinea(pol, { tipo: 'capacidad', code: c.code, costo: c.precio, cantidadUnidades: c.cantidad });
    lineas.push({ tipo: 'capacidad', code: c.code, concepto: `${c.cantidad}× ${c.code}`, ...l });
    costoMensual += l.costo;
    margenBrutoMensual += l.margen;
  }

  const lineasUnicas = [];
  let margenUnicoBruto = 0;
  for (const c of cot.cargosUnicos) {
    const l = { costo: 0, margen: r2(Math.max(0, Number(c.amount) || 0)), venta: r2(Math.max(0, Number(c.amount) || 0)) };
    lineasUnicas.push({ tipo: 'servicio', code: c.code || null, concepto: c.name || 'Cargo único', ...l });
    margenUnicoBruto += l.margen;
  }
  for (const c of cot.cargosRecurrentes) {
    const l = { costo: 0, margen: r2(Math.max(0, Number(c.mensualizado) || 0)), venta: r2(Math.max(0, Number(c.mensualizado) || 0)) };
    lineas.push({ tipo: 'servicio', code: c.code || null, concepto: c.name || 'Cargo recurrente', ...l });
    margenBrutoMensual += l.margen;
  }

  let margenNetoMensual = margenBrutoMensual;
  const descuentoMensual = cot.descuento && Number(cot.descuento.monto) > 0 ? r2(cot.descuento.monto) : 0;
  if (descuentoMensual > 0) {
    if (descuentoMensual > margenBrutoMensual + 0.005) {
      const err = new Error('El descuento negociado supera el margen del asesor sobre esta cotización.');
      err.code = DESCUENTO_EXCEDE_MARGEN;
      err.detalle = { descuentoMensual, margenBrutoMensual: r2(margenBrutoMensual) };
      throw err;
    }
    margenNetoMensual = r2(margenBrutoMensual - descuentoMensual);
    lineas.push({
      tipo: 'descuento', code: null,
      concepto: 'Descuento' + (cot.descuento.motivo ? ` · ${cot.descuento.motivo}` : ''),
      costo: 0, margen: -descuentoMensual, venta: -descuentoMensual,
    });
  }

  const costoMensualR = r2(costoMensual);
  const margenMensualR = r2(margenNetoMensual);
  const margenUnicoR = r2(margenUnicoBruto);

  return {
    politicaId: pol?._id || null,
    revision: pol?.revision || null,
    mensual: { costo: costoMensualR, margen: margenMensualR, venta: r2(costoMensualR + margenMensualR) },
    unico: { costo: 0, margen: margenUnicoR, venta: margenUnicoR },
    lineas: [...lineas, ...lineasUnicas],
  };
}

/** Formato de dinero corto para las UIs del portal. */
export function fmtMoneda(n, moneda = 'USD') {
  const v = Number(n) || 0;
  try {
    return new Intl.NumberFormat('es-SV', { style: 'currency', currency: moneda, minimumFractionDigits: 2 }).format(v);
  } catch {
    return `$${v.toFixed(2)}`;
  }
}
